import { useCoachStateQuery } from '@bridebook/web/lib/ai-planner/hooks/use-coach-state';
import { usePlanningBlueprint } from '@bridebook/web/lib/ai-planner/hooks/use-planning-blueprint';
import { resolveChecklist } from '@bridebook/web/lib/ai-planner/planning/blueprint/resolve-checklist';
import type {
  PlanningBlueprint,
  ResolvedChecklist,
} from '@bridebook/web/lib/ai-planner/planning/blueprint/types';
import { useSelector } from '@bridebook/web/lib/utils';
import { getProfile, getWeddingId, isSameSexWedding } from '@bridebook/web/lib/weddings/selectors';
import { useMemo } from 'react';

/**
 * The couple's checklist, real or projected, resolved against the country blueprint. Null
 * while either is still loading. Settled once every source it reads has finished its first
 * load, whatever the outcome, so a reader can tell "not yet" from "not available"; failed
 * once one of them came back empty-handed and will not settle on its own.
 */
export const useResolvedChecklist = (): {
  checklist: ResolvedChecklist | null;
  blueprint: PlanningBlueprint | null;
  settled: boolean;
  failed: boolean;
} => {
  const wedding = useSelector(getProfile);
  const weddingId = useSelector(getWeddingId) || undefined;
  const tasks = useSelector((state) => state.checklist.tasks);
  const checklistLoaded = useSelector((state) => state.checklist.loaded);
  const checklistFailed = useSelector((state) => state.checklist.failed);
  const sameSexRoles = useSelector(isSameSexWedding);
  const { blueprint, settled: blueprintSettled, failed: blueprintFailed } = usePlanningBlueprint();
  const {
    coachState: { projectionAnchorAt },
    settled: coachSettled,
    failed: coachFailed,
  } = useCoachStateQuery(weddingId);
  const initializedAt = wedding?.tasks?.initializedAt ?? null;

  const checklist = useMemo(
    () =>
      resolveChecklist({
        blueprint,
        weddingTasks: tasks,
        checklistLoaded,
        date: wedding?.date,
        country: wedding?.l10n?.country,
        initializedAt,
        roles: wedding?.roles,
        isSameSexRoles: sameSexRoles,
        projectionAnchorAt,
      }),
    [blueprint, tasks, checklistLoaded, wedding, initializedAt, sameSexRoles, projectionAnchorAt],
  );

  const settled = checklistLoaded && blueprintSettled && coachSettled;
  const failed = checklistFailed || blueprintFailed || coachFailed;
  return useMemo(
    () => ({ checklist, blueprint, settled, failed }),
    [checklist, blueprint, settled, failed],
  );
};
